import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import flagsmith from '@flagsmith/flagsmith';

import type { ILead } from 'src/services/api';
import {
    Auth,
    useAchievements,
    useConversations,
    useLeads,
    useLoggedInUser,
    useNotifications,
    usePollConversations,
    usePollLeads,
    useProperties,
    useTeamSettings,
    useUserSettings,
    isNonLeasedPropertyNotification
} from 'src/services/api';
import { normalizeSource } from 'src/pages/landlord/analytics/utils';
import LandlordLayout from 'src/components/layout/landlord/LandlordLayout';
import { AuthenticatedStateContext } from 'src/authenticated-state/context';
import { usePersistentState } from 'src/services/storage/use-persistent-state';
import { Config } from 'src/config';

type Props = {
    children: React.ReactNode;
};

export default function AuthenticatedStateProvider({ children }: Props) {
    const isLoggedIn = !!Auth.accessToken;
    const init = useRef(false);
    const isSentryReplaySampled = useRef<boolean | null>(null);

    const navigate = useNavigate();
    const location = useLocation();
    // showArchived is a leads page concern only — do not use it to filter conversations.
    const [showArchived, setShowArchived] = useState(false);
    const [isInactivePropertiesModalClosed, setIsInactivePropertiesModalClosed] = usePersistentState(
        'inactivePropertiesClosed',
        false
    );

    const { data: loggedInUser } = useLoggedInUser();
    const { data: teamSettings } = useTeamSettings();
    const { data: userSettings } = useUserSettings();
    const { data: achievements } = useAchievements();

    const jwtPayload = Auth.jwtPayload;
    const isBypassPaywall = jwtPayload?.ibp;
    const isPaywallEnabled = !isBypassPaywall && !loggedInUser?.team?.is_active;

    // Kept here to seed the query cache and drive refetches (e.g. on showArchived change).
    // Not exposed in context — use useConversations() directly.
    const {
        data: conversations,
        refetch: refetchConversations,
        isLoading: isConversationLoading,
        isFetched: isConversationFetched,
    } = useConversations({
        disabled: !loggedInUser?.is_onboarding_complete || isPaywallEnabled,
    });

    const {
        data: leads,
        dataUpdatedAt: leadsUpdatedAt,
        refetch: refetchLeads,
        isLoading: isLeadsLoading,
    } = useLeads({
        disabled: !loggedInUser?.is_onboarding_complete || isPaywallEnabled,
        isArchived: showArchived ? true : false,
        includeCoapplicants: true,
    });
    usePollConversations();
    usePollLeads();

    const leadIngestionSources = useMemo(() => {
        if (!leads) { return []; }

        const sources = new Set<string>();
        leads.forEach((lead) => {
            sources.add(normalizeSource(lead.ingestion_source));
        });

        return Array.from(sources).sort();
    }, [leads]);

    const leadsByUuid = useMemo(() => {
        if (!leads) { return new Map(); }

        return leads.reduce((acc, lead) => {
            acc.set(lead.uuid, lead);
            return acc;
        }, new Map<string, ILead>());
    }, [leads]);

    // prefetch properties
    const { data: properties } = useProperties({
        disabled: !loggedInUser?.is_onboarding_complete || isPaywallEnabled,
    });

    useEffect(() => {
        const onLogout = () => flagsmith.logout();
        document.addEventListener('reffie_logout', onLogout);
        return () => document.removeEventListener('reffie_logout', onLogout);
    }, []);

    useEffect(() => {
        if (!loggedInUser?.id || !loggedInUser.uuid) { return; }
        if (!loggedInUser?.team) { return; }

        /**
         * Using uuid instead of id because helps quickly differentiate between local and non-local users
         */
        flagsmith.identify(loggedInUser.uuid, {
            user_uuid: loggedInUser.uuid,
            user_id: loggedInUser.id,
            leasing_team_id: loggedInUser.leasing_team_id,
            leasing_team_name: loggedInUser.team.name,
            /**
             * Helps differentiate between local users running against staging/prod vs actual users in those environments
             */
            is_local_dev: Config.isLocal,
        });
    }, [loggedInUser?.uuid, loggedInUser?.id, loggedInUser?.leasing_team_id, loggedInUser?.team?.name]);

    /**
     * TODO: replace with feature flag + segment in subsequent PR
     * e.g. const flags = useFlags([EFeatureFlag.SENTRY_SESSION_REPLAY]);
     */
    const REPLAY_USER_IDS = (import.meta.env.VITE_SENTRY_REPLAY_USER_IDS ?? '').split(',').map(Number);
    const REPLAY_ALL_PATH_USER_IDS = (import.meta.env.VITE_SENTRY_REPLAY_ALL_PATH_USER_IDS ?? '').split(',').map(Number);
    const REPLAY_PATH = import.meta.env.VITE_SENTRY_REPLAY_PATH;
    const REPLAY_SAMPLE_RATE = Number(import.meta.env.VITE_SENTRY_REPLAY_SAMPLE_RATE ?? 0);

    useEffect(() => {
        if (!loggedInUser) { return; }

        const isTargetedUser = REPLAY_USER_IDS.includes(loggedInUser.id) && location.pathname.startsWith(REPLAY_PATH);
        const isAllPathUser = REPLAY_ALL_PATH_USER_IDS.includes(loggedInUser.id);

        if (isSentryReplaySampled.current === null) {
            isSentryReplaySampled.current = Math.random() < REPLAY_SAMPLE_RATE;
        }

        if ((isTargetedUser || isAllPathUser) && isSentryReplaySampled.current) {
            const replay = Sentry.getReplay();
            if (replay && !replay.getReplayId()) {
                replay.start();
            }
        }
    }, [loggedInUser, location.pathname]);

    // Refetch leads and conversations when showArchived changes
    useEffect(() => {
        if (!Auth.accessToken) { return; }

        if (!loggedInUser?.is_onboarding_complete) { return; }

        if (!init.current) {
            init.current = true;
            return;
        }

        refetchLeads();
        refetchConversations();
    }, [loggedInUser, showArchived]);

    const { data: notifications } = useNotifications(!isLoggedIn);

    const isInactivePropertiesModalOpen = useMemo(() => {
        if (isInactivePropertiesModalClosed || !notifications?.length || !properties) {
            return false;
        }

        const validNotifications = notifications
            .filter(notification => !notification.is_read_by_user)
            .filter(isNonLeasedPropertyNotification)
            .filter(notification => {
                const property = properties.find(p => p.id === notification.payload.property_id);
                return !!property;
            });

        return validNotifications.length > 0;
    }, [isInactivePropertiesModalClosed, notifications, properties]);

    if (!Auth.accessToken) {
        navigate('/landlord/login');
        return null;
    }

    if (!loggedInUser || !teamSettings || !userSettings) {
        return (
            <LandlordLayout isLoading />
        );
    }

    if (!loggedInUser?.is_onboarding_complete) {
        return <Navigate to="/landlord/onboard" replace />;
    }

    const areLeadsOrConversationsLoading = !isPaywallEnabled && (!leads || !conversations);
    const isAuthenticatedDataLoading = !loggedInUser || !teamSettings || !notifications || areLeadsOrConversationsLoading || isLeadsLoading || isConversationLoading || !isConversationFetched;
    const isLoading = isLoggedIn && isAuthenticatedDataLoading;
    return (
        <AuthenticatedStateContext.Provider
            value={{
                showArchived,
                setShowArchived,

                loggedInUser,
                teamSettings,
                userSettings,

                leads: leads || [],
                leadsUpdatedAt,
                leadsByUuid,
                leadIngestionSources,

                notifications: notifications || [],
                achievements: achievements || { achievements: [], user_achievements: [] },

                isPaywallEnabled,

                isInactivePropertiesModalOpen,
                setIsInactivePropertiesModalClosed,

                isLoading,
            }}
        >
            {(isLoading || !loggedInUser) ? (
                <LandlordLayout isLoading />
            ) : (
                children
            )}
        </AuthenticatedStateContext.Provider>
    );
}
